import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "../styles/login-register.css";

import Cookies from "js-cookie";
import { useUser } from "../components/context/UserContext";
import ownerOtpLogin from "../services/owner-services/owner-login-services";

import {
  validateContactNumber,
  validateOtp,
} from "../utils/validation/validate-login";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

const OwnerLogin = () => {
  const [mobileNumber, setMobileNumber] = useState("+91");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [isOtpScreen, setIsOtpScreen] = useState(false);
  const [apiError, setApiError] = useState(""); // <-- use apiError instead of error
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResendOtp, setCanResendOtp] = useState(false);

  const { setAuthentication } = useUser();
  const navigate = useNavigate();

  const startResendTimer = () => {
    setCanResendOtp(false);
    setResendTimer(60);
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResendOtp(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleRequestOtp = async () => {
    const validationError = validateContactNumber(mobileNumber);
    if (validationError) {
      setApiError(validationError);
      return;
    }

    setApiError("");
    setIsSubmitting(true);
    try {
      const cleanNumber = mobileNumber.replace(/^\+/, "").replace(/\s/g, "");
      const response = await ownerOtpLogin.requestOtp(cleanNumber);

      if (response?.data?.isSuccess) {
        setIsOtpScreen(true);
        startResendTimer();
        setApiError("");
      } else {
        // API returned a failure
        setApiError(response?.data?.message || "Failed to send OTP.");
      }
    } catch (err) {
      // Catch network or server errors
      if (err?.response?.data?.message) {
        setApiError(err.response.data.message); // <-- show backend error
      } else {
        setApiError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value.slice(0, 1);
    setOtp(updated);
    if (value && index < 3) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const handleResendOtp = async () => {
    const cleanNumber = mobileNumber.replace(/^\+/, "").replace(/\s/g, "");
    try {
      const response = await ownerOtpLogin.resendOtp(cleanNumber);
      if (response?.data?.isSuccess) {
        startResendTimer();
        setApiError("");
      } else {
        setApiError(response?.data?.message || "Failed to resend OTP.");
      }
    } catch {
      setApiError("Something went wrong while resending OTP.");
    }
  };

  const handleVerifyOtp = async () => {
    const validationError = validateOtp(otp);
    if (validationError) {
      setApiError(validationError);
      return;
    }

    setApiError("");
    setIsSubmitting(true);

    try {
      const cleanNumber = mobileNumber.replace(/^\+/, "").replace(/\s/g, "");
      const otpValue = otp.join("");
      const response = await ownerOtpLogin.verifyOtp(cleanNumber, otpValue);

      // ✅ Access success correctly (tokens already stored in ownerOtpLogin.verifyOtp)
      if (response?.isSuccess) {
        const accessToken =
          response.result?.accessToken || Cookies.get("accessToken");
        if (accessToken) {
          setAuthentication(true);
          navigate("/owner-account/properties");
        } else {
          setApiError("Invalid token response.");
        }
      } else {
        setApiError(response?.message || "Invalid OTP. Try again.");
      }
    } catch (err) {
      setApiError("Something went wrong while verifying OTP.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnterKeyPressForPhone = (e) => {
    if (e.key === "Enter") handleRequestOtp();
  };
  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        // If current input has a value, just clear it
        const updated = [...otp];
        updated[index] = "";
        setOtp(updated);
      } else if (index > 0) {
        // Move focus to previous input
        document.getElementById(`otp-${index - 1}`).focus();
        const updated = [...otp];
        updated[index - 1] = "";
        setOtp(updated);
      }
      e.preventDefault();
    } else if (e.key === "ArrowLeft" && index > 0) {
      document.getElementById(`otp-${index - 1}`).focus();
    } else if (e.key === "ArrowRight" && index < otp.length - 1) {
      document.getElementById(`otp-${index + 1}`).focus();
    } else if (e.key === "Enter") {
      handleVerifyOtp();
    }
  };

  return (
    <div className="login-wrapper">
      <div className="loginbox">
        <div className="login-auth">
          <div className="login-auth-wrap">
            <h1>Owner Login</h1>

            {!isOtpScreen ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number</Form.Label>
                  <PhoneInput
                    international
                    defaultCountry="IN"
                    id="contact-number"
                    name="login-number"
                    maxLength="15"
                    placeholder="Enter your phone number"
                    value={mobileNumber}
                    onChange={(value) => setMobileNumber(value || "+91")}
                    onKeyDown={handleEnterKeyPressForPhone}
                    className="form-control"
                  />
                  {apiError && (
                    <div className="text-danger mb-3">{apiError}</div>
                  )}
                </Form.Group>

                <Button
                  type="button"
                  className="btn btn-theme-blue w-100"
                  onClick={handleRequestOtp}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Requesting OTP..." : "Request OTP"}
                </Button>
              </>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-center gap-2">
                    {otp.map((digit, index) => (
                      <Form.Control
                        key={index}
                        id={`otp-${index}`}
                        type="text"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="otp-input text-center"
                        style={{ width: "50px", fontSize: "22px" }}
                      />
                    ))}
                  </div>
                </Form.Group>

                {apiError && <div className="text-danger mb-3">{apiError}</div>}

                <div className="resend-button-wrraper mb-3 text-center">
                  {canResendOtp ? (
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      onClick={handleResendOtp}
                    >
                      Resend OTP
                    </button>
                  ) : (
                    <div>Resend OTP in {resendTimer}s</div>
                  )}
                </div>

                <Button
                  type="button"
                  className="btn btn-theme-blue w-100"
                  onClick={handleVerifyOtp}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Verifying..." : "Verify OTP"}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerLogin;
